/**
 * Bandeau photos léger pour facility / pitch / relais.
 */

import { useRef, useState } from 'react'
import { normalizePhotoUrl } from '../../../shared/api/runtimeConfig'
import type { EntityPhoto } from '../services/organizationApi'
import './EntityPhotosStrip.css'
import { useTranslation } from 'react-i18next'

type Props = {
  photos: EntityPhoto[]
  onUpload: (file: File) => Promise<void>
  onDelete: (photoId: number) => Promise<void>
  disabled?: boolean
}

export function EntityPhotosStrip({ photos, onUpload, onDelete, disabled }: Props) {
  const { t } = useTranslation('organizations')
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !file.type.startsWith('image/')) return
    setError(null)
    setBusy(true)
    try {
      await onUpload(file)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('EntityPhotosStrip.upload_impossible'))
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm(t('EntityPhotosStrip.supprimer_photo'))) return
    setError(null)
    setBusy(true)
    try {
      await onDelete(id)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('EntityPhotosStrip.suppression_impossible'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="entity-photos">
      <div className="entity-photos__row">
        {photos.map((p) => {
          const src = normalizePhotoUrl(p.url || '') || ''
          return (
            <div key={p.id} className="entity-photos__thumb">
              {src ? (
                <img src={src} alt={p.caption || ''} />
              ) : (
                <span className="entity-photos__ph">—</span>
              )}
              <button
                type="button"
                className="entity-photos__del"
                disabled={disabled || busy}
                onClick={() => void handleDelete(p.id)}
                aria-label={t('EntityPhotosStrip.supprimer_photo_2')}
              >
                ×
              </button>
            </div>
          )
        })}
        <button
          type="button"
          className="entity-photos__add"
          disabled={disabled || busy}
          onClick={() => inputRef.current?.click()}
        >
          {busy ? '…' : t('EntityPhotosStrip.photo')}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => void handleFile(e)}
        />
      </div>
      {error && (
        <p className="entity-photos__error" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
